import { useEffect, useMemo, useState } from "react";
import {
  FileText, Download, Loader2, CheckCircle2, Mail, PenLine, Upload,
  Circle, Layers, ExternalLink, Users, Package, Sparkles, ChevronRight,
  HardHat, GraduationCap, User as UserIcon,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { SentDocumentsPanel } from "@/components/SentDocumentsPanel";

const STATUS_META = {
  missing:    { icon: Circle,        label: "Ausstehend", color: "text-zinc-500" },
  generated:  { icon: FileText,      label: "Erzeugt",    color: "text-emerald-300" },
  sent:       { icon: Mail,          label: "Versendet",  color: "text-sky-300" },
  signed:     { icon: PenLine,       label: "Unterschrieben", color: "text-violet-300" },
  uploaded:   { icon: Upload,        label: "Hochgeladen", color: "text-cyan-300" },
};

const CATEGORY_TITLES = {
  angebot:   "Angebote",
  vertrag:   "Verträge",
  rechnung:  "Rechnungen",
  protokoll: "Protokolle",
  uebergabe: "Übergabe / Installation",
  kunde:     "Kunden-Kommunikation",
  sonstige:  "Sonstige",
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return iso; }
};

/**
 * Iter89m (2026-02-21) — Projekt-Dokumenten-Cockpit.
 *
 * Zeigt für ein Projekt gruppiert nach Kategorie:
 *   - Welche Vorlagen existieren
 *   - Welche Dokumente daraus bereits erzeugt wurden
 *   - Status pro Vorlage (missing / generated / sent / signed / uploaded)
 *
 * Datenquelle: `GET /api/projects/{id}/documents-cockpit`
 * Die Aggregation greift transparent auf die bestehenden Datenmodelle
 * (offers, invoices, project.bza, project.commissioning, …) zurück, ohne
 * Duplikation.
 */
export default function ProjectDocumentsCockpit({ projectId }) {
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [packages, setPackages] = useState([]);
  const [wizardHint, setWizardHint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMissing, setShowMissing] = useState(true);
  const [packageModal, setPackageModal] = useState(null);
  const [wizardOpenKey, setWizardOpenKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [cockpit, pkgs] = await Promise.all([
        api.get(`/projects/${projectId}/documents-cockpit`),
        api.get(`/projects/${projectId}/document-packages`),
      ]);
      setData(cockpit.data);
      setPackages(pkgs.data.items || []);
      try {
        const t = await api.get(`/projects/${projectId}/timeline`);
        setTimeline(t.data);
      } catch {
        setTimeline(null);
      }
      // Iter89r — Auto-Wizard-Hint pro Stage
      try {
        const h = await api.get(`/projects/${projectId}/wizard-hint`);
        setWizardHint(h.data);
      } catch {
        setWizardHint(null);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (projectId) load(); }, [projectId]);

  const stats = useMemo(() => {
    if (!data?.entries) return { total: 0, generated: 0, sent: 0, missing: 0 };
    const counts = { total: 0, generated: 0, sent: 0, missing: 0 };
    data.entries.forEach((e) => {
      counts.total++;
      if (e.status === "missing") counts.missing++;
      else if (e.status === "sent" || e.status === "signed") counts.sent++;
      else counts.generated++;
    });
    return counts;
  }, [data]);

  if (loading) {
    return (
      <div className="p-6 text-zinc-400 inline-flex items-center gap-2 text-sm">
        <Loader2 className="animate-spin" size={14} /> Lade Dokumenten-Cockpit …
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="project-docs-cockpit">
      <SentDocumentsPanel projectId={projectId} />

      {/* Timeline (Phase 1c) */}
      {timeline && <ProjectTimeline timeline={timeline} projectId={projectId} />}

      {/* Iter89r — Auto-Wizard-Trigger Banner */}
      {wizardHint?.recommended && (
        <div
          className="bg-gradient-to-br from-amber-500/15 via-[#1a1510] to-[#0F0F12] border-2 border-amber-500/50 rounded-2xl p-4 flex items-start gap-3"
          data-testid="wizard-auto-trigger-banner"
        >
          <div className="h-10 w-10 rounded-full bg-amber-500/20 border border-amber-500/50 flex items-center justify-center flex-shrink-0">
            <Sparkles size={18} className="text-amber-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-[14px] text-amber-100">
              Nächstes Dokument vorbereiten
            </div>
            <div className="text-[12.5px] text-amber-200/80 mt-0.5 leading-snug">
              {(() => {
                const rec = wizardHint.candidates.find((c) => c.template_key === wizardHint.recommended);
                if (!rec) return null;
                const name = (data?.entries || []).find((e) => e.template_key === rec.template_key)?.template_name || rec.template_key;
                return (
                  <>
                    <b>{name}</b> — {rec.autofilled_count}/{rec.total_fields} Felder sind schon aus dem Projekt gefüllt.
                    Noch <b className="text-amber-100">{rec.missing_count}</b> Pflichtfeld{rec.missing_count === 1 ? "" : "er"} zu ergänzen.
                  </>
                );
              })()}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setWizardOpenKey(wizardHint.recommended)}
            data-testid="wizard-auto-trigger-open"
            className="h-10 px-4 rounded-lg bg-amber-500 hover:bg-amber-400 text-[#09090B] font-bold text-[13px] inline-flex items-center gap-1.5 flex-shrink-0"
          >
            <Sparkles size={13} /> Jetzt ergänzen
          </button>
        </div>
      )}

      {/* Header + Stats */}
      <div className="bg-gradient-to-br from-[#0F1E18] to-[#0F0F12] border border-[#00E575]/25 rounded-2xl p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="h-11 w-11 rounded-xl bg-[#00E575]/15 border border-[#00E575]/40 flex items-center justify-center flex-shrink-0">
            <Layers size={20} className="text-[#00E575]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-base font-bold leading-tight">Dokumente & Kommunikation</h3>
            <p className="text-[11.5px] text-emerald-300 font-semibold tracking-wider mt-0.5">
              PROJEKT-COCKPIT · {stats.total} VORLAGEN
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Vorlagen gesamt" value={stats.total} color="zinc" />
          <StatCard label="Erzeugt" value={stats.generated} color="emerald" />
          <StatCard label="Versendet / Unterschrieben" value={stats.sent} color="sky" />
          <StatCard label="Ausstehend" value={stats.missing} color="amber" />
        </div>

        <div className="flex items-center justify-between mt-4 text-[12.5px]">
          <label className="inline-flex items-center gap-2 cursor-pointer text-zinc-300">
            <input
              type="checkbox"
              checked={showMissing}
              onChange={(e) => setShowMissing(e.target.checked)}
              data-testid="docs-toggle-missing"
              className="accent-[#00E575]"
            />
            Ausstehende Vorlagen einblenden
          </label>
          <span className="text-zinc-400">{data.total_documents} Dokumente insgesamt archiviert</span>
        </div>
      </div>

      {/* Dokumenten-Pakete */}
      {packages.length > 0 && (
        <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5" data-testid="docs-packages">
          <div className="flex items-baseline justify-between mb-3">
            <h4 className="font-display font-bold text-[13px] uppercase tracking-wider text-zinc-100">
              Dokumenten-Pakete <span className="text-zinc-500 font-normal">· {packages.length}</span>
            </h4>
            <span className="text-[11px] text-zinc-500 font-mono">Empfänger-Bündel für einen Vorgang</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {packages.map((p) => (
              <PackageCard key={p.package.id} data={p} onOpen={() => setPackageModal(p.package.key)} />
            ))}
          </div>
        </section>
      )}

      {/* Gruppen nach Kategorie */}
      {(data.categories || []).map((cat) => {
        const entries = showMissing
          ? cat.entries
          : cat.entries.filter((e) => e.status !== "missing");
        if (!entries.length) return null;
        return (
          <section key={cat.key} className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5" data-testid={`docs-cat-${cat.key}`}>
            <h4 className="font-display font-bold text-[13px] uppercase tracking-wider text-zinc-100 mb-3">
              {CATEGORY_TITLES[cat.key] || cat.label} <span className="text-zinc-500 font-normal">· {entries.length}</span>
            </h4>
            <div className="space-y-2.5">
              {entries.map((e) => (
                <TemplateRow
                  key={e.template_id}
                  entry={e}
                  projectId={projectId}
                  onRefresh={load}
                />
              ))}
            </div>
          </section>
        );
      })}

      {/* Package Modal */}
      {packageModal && (
        <PackageModal
          projectId={projectId}
          packageKey={packageModal}
          onClose={() => setPackageModal(null)}
        />
      )}

      {/* Iter89r — Auto-Triggered Wizard aus dem Banner */}
      {wizardOpenKey && (() => {
        const entry = (data?.entries || []).find((e) => e.template_key === wizardOpenKey);
        return (
          <WizardModal
            projectId={projectId}
            templateKey={wizardOpenKey}
            templateName={entry?.template_name || wizardOpenKey}
            onClose={() => setWizardOpenKey(null)}
            onSuccess={() => { setWizardOpenKey(null); load(); }}
          />
        );
      })()}
    </div>
  );
}


// ─── Timeline-Widget (Phase 1c + Foto-Overlay Iter89o) ────────────────────
function ProjectTimeline({ timeline, projectId }) {
  const stages = timeline?.stages || [];
  if (!stages.length) return null;
  const completedCount = stages.filter((s) => s.completed).length;
  const pct = Math.round((completedCount / stages.length) * 100);
  const nextStage = stages.find((s) => s.is_current) || stages.find((s) => !s.completed);
  const nextAction = timeline?.next_action;
  const photoUrl = (photoId) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/photos/${encodeURIComponent(photoId)}`;
  return (
    <section
      className="bg-gradient-to-br from-[#0F0F12] via-[#12151A] to-[#0F0F12] border border-zinc-800 rounded-2xl p-4"
      data-testid="project-timeline-widget"
    >
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="font-display font-bold text-[13px] uppercase tracking-wider text-zinc-100">
            Projekt-Zeitstrahl
          </div>
          <div className="text-[11px] text-emerald-300 font-mono mt-0.5">
            {completedCount} / {stages.length} Schritte · {pct}% abgeschlossen
          </div>
        </div>
        {nextStage && (
          <div className="text-right text-[11.5px] max-w-[60%]">
            <div className="text-zinc-400 font-mono uppercase tracking-wider text-[10px]">Nächster Schritt</div>
            <div className="text-emerald-300 font-semibold truncate">{nextStage.label}</div>
          </div>
        )}
      </div>

      {/* Horizontale Stage-Chain mit Foto-Overlay pro Stage */}
      <div className="relative overflow-x-auto pb-1">
        <div className="flex items-start gap-1 min-w-max">
          {stages.map((s, idx) => {
            const done = s.completed;
            const active = s.is_current;
            return (
              <div key={s.key} className="flex items-start" data-testid={`timeline-stage-${s.key}`}>
                {idx > 0 && (
                  <div className={`h-[2px] w-6 sm:w-10 mt-3 ${
                    done ? "bg-[#00E575]" : "bg-zinc-800"
                  }`} />
                )}
                <div className="flex flex-col items-center max-w-[92px]">
                  {/* Foto-Miniaturen ÜBER dem Stage-Kreis */}
                  {(s.photos || []).length > 0 && (
                    <div className="flex -space-x-1.5 mb-1.5" data-testid={`timeline-photos-${s.key}`}>
                      {s.photos.slice(0, 3).map((ph) => (
                        <img
                          key={ph.id}
                          src={photoUrl(ph.id)}
                          alt={ph.caption || ""}
                          loading="lazy"
                          title={ph.caption || ""}
                          className={`h-8 w-8 rounded-full border-2 object-cover ${
                            done ? "border-[#00E575]" : "border-zinc-700"
                          }`}
                        />
                      ))}
                    </div>
                  )}
                  <div
                    className={`h-6 w-6 rounded-full flex items-center justify-center border-2 ${
                      done
                        ? "bg-[#00E575] border-[#00E575] text-[#09090B]"
                        : active
                        ? "bg-[#0E0E12] border-[#00E575] text-[#00E575] animate-pulse"
                        : "bg-[#0E0E12] border-zinc-700 text-zinc-600"
                    }`}
                  >
                    {done
                      ? <CheckCircle2 size={14} strokeWidth={3} />
                      : <span className="text-[10px] font-bold">{idx + 1}</span>}
                  </div>
                  <div
                    className={`text-[9.5px] text-center mt-1 leading-tight uppercase tracking-wider ${
                      done ? "text-zinc-300" : active ? "text-emerald-300 font-bold" : "text-zinc-600"
                    }`}
                    style={{ wordBreak: "break-word" }}
                  >
                    {s.short_label || s.label}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {nextAction && (
        <div className="mt-3 flex items-center gap-2 text-[12px] bg-emerald-500/8 border border-emerald-500/30 rounded-lg px-3 py-2">
          <Sparkles size={13} className="text-emerald-300 flex-shrink-0" />
          <div className="text-zinc-200">
            <b className="text-emerald-300">Nächste Aktion:</b> {nextAction}
          </div>
        </div>
      )}
    </section>
  );
}


// ─── Paket-Karte (Phase 1b, Iter89n) ──────────────────────────────────────
const RECIPIENT_ICONS = {
  kunde:          UserIcon,
  installateur:   HardHat,
  energieberater: GraduationCap,
};

function PackageCard({ data, onOpen }) {
  const pkg = data.package;
  const Icon = RECIPIENT_ICONS[pkg.recipient] || Package;
  const ready = data.ready;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`docs-package-${pkg.key}`}
      className={`text-left rounded-xl border p-3 transition group ${
        ready
          ? "bg-emerald-500/8 border-emerald-500/40 hover:border-emerald-400"
          : "bg-[#09090B] border-zinc-800 hover:border-zinc-600"
      }`}
    >
      <div className="flex items-start gap-2.5">
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
          ready ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-800 text-zinc-400"
        }`}>
          <Icon size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[13px] text-zinc-100 truncate">{pkg.name}</div>
          <div className="text-[11px] text-zinc-500 line-clamp-1 mt-0.5">{pkg.description}</div>
          <div className="flex items-baseline gap-2 mt-1.5 text-[11px]">
            {ready ? (
              <span className="text-emerald-300 font-semibold inline-flex items-center gap-1">
                <CheckCircle2 size={10} /> Vollständig · {data.total_documents} Dok
              </span>
            ) : (
              <span className="text-amber-300 font-semibold">
                {data.missing_count} fehlt · {data.total_documents} vorhanden
              </span>
            )}
          </div>
        </div>
        <ChevronRight size={14} className="text-zinc-500 group-hover:text-zinc-300 mt-1 flex-shrink-0" />
      </div>
    </button>
  );
}


function PackageModal({ projectId, packageKey, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api
      .get(`/projects/${projectId}/document-packages/${packageKey}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [projectId, packageKey]);

  const openAll = async () => {
    const docs = (data?.entries || []).flatMap((e) => e.documents || []);
    for (const d of docs) {
      if (!d.storage_path) continue;
      try {
        const r = await api.get(`/files/${encodeURI(d.storage_path)}`, { responseType: "blob" });
        const url = URL.createObjectURL(r.data);
        window.open(url, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(url), 15000);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Package doc open failed", e);
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="package-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#18181B] border border-zinc-700 rounded-2xl max-w-xl w-full max-h-[85vh] overflow-hidden flex flex-col"
      >
        {loading ? (
          <div className="p-8 text-center text-zinc-400 inline-flex items-center justify-center gap-2">
            <Loader2 className="animate-spin" size={16} /> Lade Paket …
          </div>
        ) : (
          <>
            <div className="p-5 border-b border-zinc-800">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-display text-lg font-bold text-zinc-100 leading-tight">
                    {data.package.name}
                  </h3>
                  <p className="text-[12.5px] text-zinc-400 mt-1">{data.package.description}</p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="h-8 w-8 rounded-full bg-zinc-800 text-zinc-300 hover:bg-zinc-700 flex items-center justify-center flex-shrink-0"
                  aria-label="Schließen"
                >
                  ×
                </button>
              </div>
              <div className="flex items-center gap-3 mt-3 text-[12px]">
                <span className={`inline-flex items-center gap-1 font-semibold ${
                  data.ready ? "text-emerald-300" : "text-amber-300"
                }`}>
                  {data.ready
                    ? <><CheckCircle2 size={12} /> Alle {data.entries.length} Dokumente vorhanden</>
                    : <>{data.missing_count} von {data.entries.length} fehlen noch</>}
                </span>
                <span className="text-zinc-500">·</span>
                <span className="text-zinc-400">{data.total_documents} Snapshots archiviert</span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {data.entries.map((e, i) => (
                <div key={e.template_key + i} className="bg-[#0F0F12] border border-zinc-800 rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-[13px] text-zinc-100 truncate">{e.template_name}</div>
                      <div className="text-[11px] text-zinc-500">
                        {e.status === "missing" ? "Noch nicht erzeugt" : `${e.count}× vorhanden`}
                      </div>
                    </div>
                    <span className={`text-[10.5px] px-2 py-0.5 rounded font-mono uppercase tracking-wider ${
                      e.status === "missing" ? "bg-amber-500/15 text-amber-300" :
                      e.status === "sent" ? "bg-sky-500/15 text-sky-300" :
                      e.status === "signed" ? "bg-violet-500/15 text-violet-300" :
                      "bg-emerald-500/15 text-emerald-300"
                    }`}>
                      {e.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-zinc-800 bg-[#0F0F12] flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 h-10 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[13px] font-semibold"
              >
                Schließen
              </button>
              <button
                type="button"
                onClick={openAll}
                disabled={data.total_documents === 0}
                data-testid="package-open-all"
                className="flex-1 h-10 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] hover:bg-[#00C964] disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
              >
                <ExternalLink size={13} /> Alle {data.total_documents} öffnen
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function StatCard({ label, value, color }) {
  const colorClasses = {
    zinc:    "bg-zinc-500/10 text-zinc-100 border-zinc-500/30",
    emerald: "bg-emerald-500/10 text-emerald-200 border-emerald-500/30",
    sky:     "bg-sky-500/10 text-sky-200 border-sky-500/30",
    amber:   "bg-amber-500/10 text-amber-200 border-amber-500/30",
  };
  return (
    <div className={`rounded-xl border p-3 ${colorClasses[color] || colorClasses.zinc}`}>
      <div className="text-[10.5px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
      <div className="font-mono text-xl font-bold mt-1">{value}</div>
    </div>
  );
}


function TemplateRow({ entry, projectId, onRefresh }) {
  const meta = STATUS_META[entry.status] || STATUS_META.missing;
  const StatusIcon = meta.icon;
  const [wizardOpen, setWizardOpen] = useState(false);
  const wizardEligible = entry.status === "missing" &&
    ["auftragsbestaetigung", "commissioning_protocol", "liefer_leistungsvertrag",
     "installer_handover", "vcheck_wp", "orientation_offer_wp", "bza_upload"].includes(entry.template_key);

  return (
    <div
      className={`rounded-xl border p-3 ${
        entry.status === "missing"
          ? "bg-[#09090B] border-zinc-800/60"
          : "bg-[#09090B] border-zinc-700"
      }`}
      data-testid={`doc-row-${entry.template_key}`}
    >
      <div className="flex items-start gap-3">
        <StatusIcon size={16} className={`flex-shrink-0 mt-0.5 ${meta.color}`} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <div className="font-semibold text-[13.5px] text-zinc-100">{entry.template_name}</div>
            <span className={`text-[10.5px] uppercase tracking-wider font-mono ${meta.color}`}>
              {meta.label}
            </span>
            {entry.count > 0 && (
              <span className="text-[10.5px] text-zinc-500 font-mono">{entry.count}× archiviert</span>
            )}
          </div>
          {entry.description && (
            <p className="text-[12px] text-zinc-500 mt-0.5 leading-snug line-clamp-2">
              {entry.description}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            {(entry.recipients || []).map((r) => (
              <span key={r} className="text-[10.5px] px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-300 border border-sky-500/30">
                <Users size={9} className="inline -mt-0.5" /> {r}
              </span>
            ))}
            {wizardEligible && (
              <button
                type="button"
                onClick={() => setWizardOpen(true)}
                data-testid={`wizard-open-${entry.template_key}`}
                className="text-[11px] ml-auto h-7 px-2.5 rounded-md bg-[#00E575] text-[#09090B] font-bold hover:bg-[#00C964] inline-flex items-center gap-1"
              >
                <Sparkles size={11} /> Erstellen
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Dokumente-Liste */}
      {entry.documents?.length > 0 && (
        <div className="mt-3 pl-7 space-y-1.5">
          {entry.documents.map((d) => (
            <DocumentSnapshot key={d.id + (d.storage_path || "")} doc={d} />
          ))}
        </div>
      )}

      {wizardOpen && (
        <WizardModal
          projectId={projectId}
          templateKey={entry.template_key}
          templateName={entry.template_name}
          onClose={() => setWizardOpen(false)}
          onSuccess={() => { setWizardOpen(false); onRefresh?.(); }}
        />
      )}
    </div>
  );
}


function DocumentSnapshot({ doc }) {
  const openDoc = async () => {
    try {
      const r = await api.get(`/files/${encodeURI(doc.storage_path)}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Document open failed", e);
    }
  };

  return (
    <div className="flex items-center justify-between gap-2 bg-zinc-900/60 border border-zinc-800 rounded-lg p-2" data-testid="doc-snapshot">
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-medium text-zinc-100 truncate">
          {doc.label}
        </div>
        <div className="text-[10.5px] text-zinc-500 font-mono">
          {doc.generated_at && <>Erzeugt: {fmtDate(doc.generated_at)}</>}
          {doc.sent_at && <> · <Mail size={9} className="inline -mt-0.5" /> Versendet: {fmtDate(doc.sent_at)}</>}
          {doc.signed_at && <> · <PenLine size={9} className="inline -mt-0.5" /> Unterschrieben: {fmtDate(doc.signed_at)}</>}
          {doc.paid_at && <> · <CheckCircle2 size={9} className="inline -mt-0.5 text-emerald-400" /> Bezahlt</>}
        </div>
      </div>
      {doc.storage_path && (
        <button
          type="button"
          onClick={openDoc}
          data-testid={`doc-open-${doc.id}`}
          className="h-8 px-2.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-[11.5px] font-semibold inline-flex items-center gap-1"
        >
          <ExternalLink size={11} /> Öffnen
        </button>
      )}
    </div>
  );
}


// ─── Missing-Fields-Wizard (Phase 2, Iter89o + Preview Iter89p) ───────────
function WizardModal({ projectId, templateKey, templateName, onClose, onSuccess }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    let cancel = false;
    setState({ loading: true, error: null, data: null });
    api
      .post(`/projects/${projectId}/documents/${templateKey}/prepare`)
      .then((r) => {
        if (cancel) return;
        setState({ loading: false, error: null, data: r.data });
        const initial = {};
        (r.data.fields || []).forEach((f) => {
          if (f.autofilled && f.value !== null) initial[f.key] = f.value;
        });
        setValues(initial);
      })
      .catch((e) => {
        if (cancel) return;
        setState({ loading: false, error: e.response?.data?.detail || "Fehler beim Laden", data: null });
      });
    return () => { cancel = true; };
  }, [projectId, templateKey]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/projects/${projectId}/documents/${templateKey}/generate`, { values });
      if (r.data?.ok) {
        toast.success(
          r.data.status === "draft_saved"
            ? `${templateName}: Entwurf gespeichert.`
            : `${templateName} erzeugt.`
        );
        onSuccess?.();
      } else {
        toast.error("Erzeugung fehlgeschlagen");
      }
    } catch (e) {
      const d = e.response?.data;
      if (d?.detail?.error === "still_missing") {
        toast.error(`Noch fehlend: ${(d.detail.missing || []).join(", ")}`);
      } else {
        toast.error(d?.detail || d?.error || e.message || "Fehler");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const requiredFilled = useMemo(() => {
    if (!state.data) return false;
    return (state.data.fields || []).every((f) => !f.required || (values[f.key] !== undefined && values[f.key] !== ""));
  }, [state.data, values]);

  const autofillStats = useMemo(() => {
    if (!state.data) return { auto: 0, manual: 0, total: 0 };
    const fields = state.data.fields || [];
    const auto = fields.filter((f) => f.autofilled).length;
    return { auto, manual: fields.length - auto, total: fields.length };
  }, [state.data]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="wizard-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#18181B] border border-zinc-700 rounded-2xl max-w-2xl w-full max-h-[88vh] overflow-hidden flex flex-col"
      >
        <div className="p-5 border-b border-zinc-800 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] text-emerald-300 font-mono uppercase tracking-wider">
              <Sparkles size={11} className="inline -mt-0.5" />{" "}
              {showPreview ? "Vorschau · Bitte prüfen" : "Dokument erstellen"}
            </div>
            <h3 className="font-display text-lg font-bold text-zinc-100 leading-tight mt-1">
              {templateName}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-full bg-zinc-800 text-zinc-300 hover:bg-zinc-700 flex items-center justify-center flex-shrink-0"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {state.loading ? (
            <div className="text-zinc-400 inline-flex items-center gap-2 text-sm">
              <Loader2 className="animate-spin" size={14} /> Sammle Projektdaten …
            </div>
          ) : state.error ? (
            <div className="text-rose-300 bg-rose-500/10 border border-rose-500/40 rounded-lg p-3 text-sm">
              {state.error}
            </div>
          ) : showPreview ? (
            <WizardPreview fields={state.data.fields} values={values} />
          ) : (
            <>
              <div className="text-[12px] text-zinc-400 bg-zinc-900/50 border border-zinc-800 rounded-md p-2.5">
                <b className="text-emerald-300">Auto-Fill:</b>{" "}
                {autofillStats.auto} von {autofillStats.total} Feldern automatisch aus dem Projekt übernommen.
              </div>
              {state.data.fields.map((f) => (
                <WizardField
                  key={f.key}
                  field={f}
                  value={values[f.key] ?? ""}
                  onChange={(v) => setValues((prev) => ({ ...prev, [f.key]: v }))}
                />
              ))}
            </>
          )}
        </div>

        <div className="p-4 border-t border-zinc-800 bg-[#0F0F12] flex gap-2">
          {!showPreview ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 h-10 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[13px] font-semibold"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => setShowPreview(true)}
                disabled={!state.data || !requiredFilled}
                data-testid="wizard-preview"
                className="flex-1 h-10 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] hover:bg-[#00C964] disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
              >
                <FileText size={13} /> Vorschau
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setShowPreview(false)}
                disabled={submitting}
                className="flex-1 h-10 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[13px] font-semibold"
              >
                Zurück · bearbeiten
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                data-testid="wizard-submit"
                className="flex-1 h-10 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] hover:bg-[#00C964] disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
              >
                {submitting
                  ? <><Loader2 size={13} className="animate-spin" /> Erzeuge …</>
                  : <><CheckCircle2 size={13} /> Bestätigen & erzeugen</>}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function WizardPreview({ fields, values }) {
  const rows = fields.map((f) => ({
    ...f,
    displayValue: values[f.key] ?? f.value ?? "",
  }));
  return (
    <div className="space-y-2" data-testid="wizard-preview-panel">
      <div className="text-[12px] text-emerald-300 bg-emerald-500/8 border border-emerald-500/30 rounded-md p-3 leading-snug">
        <CheckCircle2 size={13} className="inline -mt-0.5 mr-1" />
        Prüfen Sie die Werte. „Bestätigen & erzeugen" speichert die Daten am
        Projekt (einmalige Erfassung) und rendert das Dokument.
      </div>
      <div className="bg-[#09090B] border border-zinc-800 rounded-lg divide-y divide-zinc-800">
        {rows.map((r) => (
          <div key={r.key} className="flex items-start justify-between gap-3 p-3">
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">
                {r.label}
                {r.required && <span className="text-amber-400 ml-0.5">*</span>}
              </div>
              <div className="text-[13.5px] text-zinc-100 mt-0.5 break-words">
                {r.displayValue !== "" && r.displayValue !== null
                  ? String(r.displayValue)
                  : <span className="text-zinc-600 italic">— leer —</span>}
              </div>
            </div>
            {r.autofilled && values[r.key] === r.value && (
              <span className="text-[10px] font-mono bg-emerald-500/15 text-emerald-300 px-1.5 py-0.5 rounded flex-shrink-0 mt-1">
                auto
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function WizardField({ field, value, onChange }) {
  const missing = field.required && (!value || value === "");
  const label = (
    <label className="text-[11px] uppercase tracking-wider font-semibold block mb-1">
      <span className={missing ? "text-amber-300" : "text-zinc-400"}>
        {field.label}
        {field.required && <span className="text-amber-400 ml-0.5">*</span>}
      </span>
      {field.autofilled && (
        <span className="ml-2 text-[10px] font-mono bg-emerald-500/15 text-emerald-300 px-1.5 py-0.5 rounded">
          auto
        </span>
      )}
    </label>
  );
  const inputCls = `w-full h-9 px-2.5 bg-[#09090B] border rounded-md text-[13px] focus:outline-none ${
    missing ? "border-amber-500/60 focus:border-amber-400" : "border-zinc-800 focus:border-[#00E575]"
  }`;
  let input;
  if (field.type === "textarea") {
    input = (
      <textarea
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`wizard-field-${field.key}`}
        className={inputCls + " h-auto py-2"}
      />
    );
  } else if (field.type === "select") {
    input = (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`wizard-field-${field.key}`}
        className={inputCls}
      >
        <option value="">— bitte wählen —</option>
        {(field.options || []).map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  } else if (field.type === "date") {
    // ISO YYYY-MM-DD im native date-Input
    const isoValue = value ? String(value).slice(0, 10) : "";
    input = (
      <input
        type="date"
        value={isoValue}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`wizard-field-${field.key}`}
        className={inputCls}
      />
    );
  } else {
    input = (
      <input
        type={field.type === "number" ? "number" : (field.type === "email" ? "email" : "text")}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`wizard-field-${field.key}`}
        className={inputCls}
      />
    );
  }
  return (
    <div>
      {label}
      {input}
      {field.help && <p className="text-[11px] text-zinc-500 mt-1">{field.help}</p>}
    </div>
  );
}

